import React from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

// Shared period picker for every report. One place to add a preset so all the
// reports gain it at once.
const iso = (d) => {
  const t = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return t.toISOString().split('T')[0];
};
const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const startOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
const endOfQuarter = (d) => new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3 + 3, 0);

// The SA tax year runs 1 March → end of February.
const startOfTaxYear = (d) => {
  const y = d.getMonth() >= 2 ? d.getFullYear() : d.getFullYear() - 1;
  return new Date(y, 2, 1);
};

export const PRESETS = {
  today:        { label: 'Today',            range: (n) => [n, n] },
  yesterday:    { label: 'Yesterday',        range: (n) => { const d = new Date(n); d.setDate(d.getDate() - 1); return [d, d]; } },
  this_week:    { label: 'This week',        range: (n) => [startOfWeek(n), n] },
  last_7:       { label: 'Last 7 days',      range: (n) => { const d = new Date(n); d.setDate(d.getDate() - 6); return [d, n]; } },
  this_month:   { label: 'This month',       range: (n) => [startOfMonth(n), n] },
  last_month:   { label: 'Last month',       range: (n) => { const d = new Date(n.getFullYear(), n.getMonth() - 1, 1); return [d, endOfMonth(d)]; } },
  last_30:      { label: 'Last 30 days',     range: (n) => { const d = new Date(n); d.setDate(d.getDate() - 29); return [d, n]; } },
  this_quarter: { label: 'This quarter',     range: (n) => [startOfQuarter(n), n] },
  last_quarter: { label: 'Last quarter',     range: (n) => { const d = new Date(n.getFullYear(), Math.floor(n.getMonth() / 3) * 3 - 3, 1); return [d, endOfQuarter(d)]; } },
  last_90:      { label: 'Last 90 days',     range: (n) => { const d = new Date(n); d.setDate(d.getDate() - 89); return [d, n]; } },
  this_year:    { label: 'This year',        range: (n) => [new Date(n.getFullYear(), 0, 1), n] },
  last_year:    { label: 'Last year',        range: (n) => [new Date(n.getFullYear() - 1, 0, 1), new Date(n.getFullYear() - 1, 11, 31)] },
  tax_year:     { label: 'Tax year (Mar–Feb)', range: (n) => [startOfTaxYear(n), n] },
  last_12:      { label: 'Last 12 months',   range: (n) => { const d = new Date(n); d.setMonth(d.getMonth() - 12); return [d, n]; } },
  all_time:     { label: 'All time',         range: (n) => [new Date(2025, 0, 1), n] },
};

// Chips for the handful used daily; everything else lives in the dropdown.
const QUICK = ['today', 'this_week', 'this_month', 'last_month', 'last_30'];

export default function DateRangeBar({ from, to, setFrom, setTo, onChange }) {
  const apply = (key) => {
    const [f, t] = PRESETS[key].range(new Date());
    setFrom(iso(f)); setTo(iso(t));
    onChange && onChange();
  };

  // Step the window back/forward by its own length — click through months or
  // weeks without re-picking dates each time.
  const shift = (dir) => {
    const f = new Date(from), t = new Date(to);
    const days = Math.round((t - f) / 86400000) + 1;
    // A whole calendar month steps to the neighbouring month, not 30 days
    const isWholeMonth = f.getDate() === 1 && t.getDate() === endOfMonth(t).getDate() && f.getMonth() === t.getMonth();
    if (isWholeMonth) {
      const nf = new Date(f.getFullYear(), f.getMonth() + dir, 1);
      setFrom(iso(nf)); setTo(iso(endOfMonth(nf)));
    } else {
      f.setDate(f.getDate() + dir * days); t.setDate(t.getDate() + dir * days);
      setFrom(iso(f)); setTo(iso(t));
    }
    onChange && onChange();
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-center gap-2">
      <div className="flex items-center gap-2 text-sm text-gray-600 mr-1">
        <Calendar size={15} className="text-gray-400" />
        <span className="font-medium">Period:</span>
      </div>

      {QUICK.map((k) => (
        <button key={k} onClick={() => apply(k)}
          className="text-xs px-3 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:border-orange-400 transition-colors">
          {PRESETS[k].label}
        </button>
      ))}

      <select value="" onChange={(e) => e.target.value && apply(e.target.value)}
        className="border rounded-full px-3 py-1.5 text-xs text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-400">
        <option value="">More…</option>
        {Object.entries(PRESETS).filter(([k]) => !QUICK.includes(k)).map(([k, p]) => (
          <option key={k} value={k}>{p.label}</option>
        ))}
      </select>

      <div className="flex items-center gap-1 ml-auto">
        <button onClick={() => shift(-1)} title="Previous period"
          className="p-1.5 rounded border text-gray-500 hover:bg-gray-50"><ChevronLeft size={14} /></button>
        <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); onChange && onChange(); }}
          className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <span className="text-gray-400 text-xs">to</span>
        <input type="date" value={to} onChange={(e) => { setTo(e.target.value); onChange && onChange(); }}
          className="border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-orange-400" />
        <button onClick={() => shift(1)} title="Next period"
          className="p-1.5 rounded border text-gray-500 hover:bg-gray-50"><ChevronRight size={14} /></button>
      </div>
    </div>
  );
}
